import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import {
  MarketingContent,
  MarketingContentPatch,
  StageState,
  StageStatus,
  ExtraLink,
} from '../types/marketing';
import {
  WorkflowTemplate,
  initialStageStatuses,
  nextOpenStageId,
  getTemplateOrDefault,
} from './marketingTemplates';

const COLLECTION = 'marketingContent';

/** Generic Firestore payload shape; `any` is required for `updateDoc` compatibility. */
type FsRecord = Record<string, any>;

/** Normalize a Firestore doc into the MarketingContent shape used by the UI. */
function fromDoc(id: string, data: FsRecord): MarketingContent {
  return {
    id,
    title: (data.title as string) || 'Untitled',
    contentType: (data.contentType as MarketingContent['contentType']) || 'podcast-video',
    templateId: (data.templateId as string) || 'podcast-video-v1',
    currentStageId: (data.currentStageId as string) || '',
    stageStatuses: (data.stageStatuses as Record<string, StageStatus>) || {},
    recordingDate: (data.recordingDate as Timestamp | null) ?? null,
    publishDate: (data.publishDate as Timestamp | null) ?? null,
    guestName: (data.guestName as string | null) ?? null,
    guestLeadId: (data.guestLeadId as string | null) ?? null,
    dropboxRawUrl: (data.dropboxRawUrl as string | null) ?? null,
    dropboxArchiveUrl: (data.dropboxArchiveUrl as string | null) ?? null,
    ssdPath: (data.ssdPath as string | null) ?? null,
    youtubeUrl: (data.youtubeUrl as string | null) ?? null,
    thumbnailUrl: (data.thumbnailUrl as string | null) ?? null,
    notes: (data.notes as string | null) ?? null,
    extraLinks: (data.extraLinks as ExtraLink[] | undefined) ?? [],
    createdAt: (data.createdAt as Timestamp) ?? Timestamp.now(),
    updatedAt: (data.updatedAt as Timestamp) ?? Timestamp.now(),
    createdBy: (data.createdBy as string) || '',
  };
}

/** Subscribe to all marketing content, ordered newest-first. */
export function subscribeToMarketingContent(
  cb: (items: MarketingContent[]) => void
): () => void {
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    snap => {
      cb(snap.docs.map(d => fromDoc(d.id, d.data())));
    },
    err => firestoreSnapshotListenError('marketingService', 'marketingContent', err),
  );
}

export interface CreateContentInput {
  title: string;
  template: WorkflowTemplate;
  recordingDate?: Date | null;
  guestName?: string | null;
  createdBy: string;
}

/**
 * Create a new content item, initializing all template stages to 'todo'
 * and pointing currentStageId at the first stage.
 */
export async function createContent(input: CreateContentInput): Promise<string> {
  const now = Timestamp.now();
  const stages = initialStageStatuses(input.template);
  const firstStage = input.template.stages[0]?.id ?? '';

  const payload: FsRecord = {
    title: input.title.trim() || 'Untitled',
    contentType: input.template.contentType,
    templateId: input.template.id,
    currentStageId: firstStage,
    stageStatuses: stages,
    recordingDate: input.recordingDate ? Timestamp.fromDate(input.recordingDate) : null,
    publishDate: null,
    guestName: input.guestName?.trim() || null,
    guestLeadId: null,
    dropboxRawUrl: null,
    dropboxArchiveUrl: null,
    ssdPath: null,
    youtubeUrl: null,
    thumbnailUrl: null,
    notes: null,
    extraLinks: [],
    createdAt: now,
    updatedAt: now,
    createdBy: input.createdBy,
  };

  const ref = await addDoc(collection(db, COLLECTION), payload);
  return ref.id;
}

/** Convert undefined values to null and Date fields to Timestamps for Firestore. */
function sanitizeForFirestore(patch: MarketingContentPatch): FsRecord {
  const out: FsRecord = {};
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) {
      out[k] = null;
    } else if (v instanceof Date) {
      out[k] = Timestamp.fromDate(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** Patch top-level fields on a content item. Always bumps updatedAt. */
export async function updateContent(
  id: string,
  patch: MarketingContentPatch
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  const payload = sanitizeForFirestore(patch);
  payload.updatedAt = Timestamp.now();
  await updateDoc(ref, payload);
}

export interface SetStageInput {
  contentId: string;
  stageId: string;
  state: StageState;
  note?: string | null;
  userId: string;
  /** Current stageStatuses on the content (read from local state). */
  current: Record<string, StageStatus>;
  templateId: string;
}

/**
 * Update a single stage's status, then recompute currentStageId so the kanban
 * advances automatically when a stage is marked done.
 */
export async function setStageStatus(input: SetStageInput): Promise<void> {
  const { contentId, stageId, state, note, userId, current, templateId } = input;
  const template = getTemplateOrDefault(templateId);
  const now = Timestamp.now();

  const next: StageStatus = {
    status: state,
    note: note ?? current[stageId]?.note ?? null,
    completedAt: state === 'done' ? now : null,
    completedBy: state === 'done' ? userId : null,
  };

  const updatedStatuses: Record<string, StageStatus> = {
    ...current,
    [stageId]: next,
  };

  const newCurrentStageId = nextOpenStageId(template, updatedStatuses);

  const ref = doc(db, COLLECTION, contentId);
  await updateDoc(ref, {
    stageStatuses: updatedStatuses,
    currentStageId: newCurrentStageId,
    updatedAt: now,
  });
}

/** Convenience: update the per-stage note without changing status. */
export async function updateStageNote(
  contentId: string,
  stageId: string,
  current: Record<string, StageStatus>,
  note: string
): Promise<void> {
  const existing = current[stageId] ?? { status: 'todo' as StageState };
  const updated: Record<string, StageStatus> = {
    ...current,
    [stageId]: { ...existing, note: note || null },
  };
  const ref = doc(db, COLLECTION, contentId);
  await updateDoc(ref, {
    stageStatuses: updated,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteContent(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}
